// Triggers: the When… start (issue 1500; docs/MRCS/workbench.md §6, §4.3; step 5 of §11). An entry whose block says
// `when` is started by the table itself, when one of a closed list of table events happens, for each creature carrying
// the entry. Pure: the server notices the event, reads what the run needs and commits the record this builds.
//
// THE LIST (`TRIGGERS`, pinned beside `scripts/mrcs/blocks.js`'s copy, which the validator reads): each trigger names
// its roles, and its `carrier` is the role whose creature must carry the entry for it to run.
//   provokes    a token's move leaves a square threatened by a token hostile to it (by the system's reach, `weaponReach.ts`;
//               next to it where the system names none), during an encounter. Threatener (the carrier) and Provoker. Hostile is the auras'
//               side rule (`relation`, issue 1150): the party against everything else unless the DM set sides; a
//               Neutral (qa-runs#1157) is on nobody's side, so it neither draws nor makes one.
//   hit_ranged  (qa-runs#366) a ranged attack, not a spell's, becomes a hit: Hit (the carrier) and Attacker. An entry that
//               can end `resolves.kind: "negate"` holds the hit's damage until it is answered (Deflect Arrows).
//   hit_melee   an attack (attack.ts) becomes a hit and it is not ranged: as it lands, when the DM gives its defence,
//               fudges it into a hit, or changes its outcome to one, or a house rule's number makes it one — once per
//               attack whatever changes after (`from`, the attack's id: the log refuses a second record of it). An
//               attack inside a trigger's own chain that hits (an End judged `attack`, its outcome a hit, a threat or
//               a critical, with an Attacker and a Target: `struckBy`) is one too. Hit (the carrier) and Attacker.
//   turn_start, turn_end   the encounter's turn moves on: the combatant whose turn ended and the one whose turn began,
//               each of its tokens. Creature. Ending the encounter ends no turn.
//   drops       a token's hit points go from above 0 to 0 or fewer. Creature, and Cause when the table knows who did it.
//   damaged     a token takes damage, more than 0 of it after what stops it (docs/MRCS/dnd-5e.md §3.2.6). Creature, and Cause when the table knows it.
//   attacks, attacked   (issue 1508; docs/MRCS/feats.md §6) an attack is about to be rolled: Attacker (the carrier of
//               `attacks`) and Target (of `attacked`). Its entries read the attack (`attack` Directs) and may add a line to
//               it (`term`) or put an effect on a combatant until its next turn (`lasts: next_turn`, Power Attack). One
//               that asks holds the attack until it is answered (the socket); one that does not runs as the attack is made.
//   fells       a melee attack's damage drops its target to 0 or fewer: Attacker (the carrier) and Felled.
//   hits        (issue 1509) a melee attack becomes a hit, noticed as hit_melee is but from the attacker's side:
//               Attacker (the carrier) and Target. An attack inside a trigger's own chain is not one.
//               An entry started `fells` or `hits` (AGAIN_TRIGGERS) may make the attack that set it off again
//               (`resolves.kind: "attack"`, Cleave): the socket makes it, once until the attacker's next turn.
//   crits       an attack, melee or ranged, becomes a critical hit, noticed as hits is: Attacker (the carrier) and Target.
//   mount_hit   (qa-runs#366) hit_melee or hit_ranged on a creature ridden (`mounted`, follow.ts): Rider (the carrier), Hit
//               (the mount) and Attacker. The record holds the attack's total (`attack.total`); a `negate` End undoes the hit.
//   fails_save  (qa-runs#366) a creature fails a save inside a record, a template's or a chain's: Creature (the carrier).
//               An entry's `save_of` names the save it answers. Its `rethrow` End throws that save again (`save.rethrow`);
//               until it is answered the failed creature's share of the record does not settle (the socket's hold).
//
// CARRYING (`carriesEntry`). A creature carries an entry when one of the names on it — its conditions, the effects on
// its combatant, the feats its linked sheet lists (`values.feats`, a trailing ": choice" left off) — is the entry's name
// as a slug ("Fire Shield" is `fire-shield`), its id with "-" for "_", or the id of the System entry a DM's copy
// overrides (the server names it). An entry that says `carried_by: 'everyone'` (the attack of opportunity; Tom's A on
// #1500) is carried by every creature in the encounter with no name needed. A creature the DM marked "No attacks of
// opportunity" (`noAoo`), or one that is helpless (`helpless`: below 0 hit points unless Diehard or ferocity keeps it
// fighting, unconscious from nonlethal damage, or a condition such as Paralyzed), in a grapple (Grappling, Grappled,
// Pinned), or flat-footed by the system's rule (`opportunity.until_acted`: the condition it names, or round 1 before
// its turn) without a name that rule excepts (Combat Reflexes), carries no entry started from `provokes`, however named.
//
// ROLES. A role picked `trigger` takes the event's creature of the same key; a role picked `self` is the carrier.
//
// THE RECORD (`TriggerFacts`, on a roll record's `trigger`): the blocks the run reaches, the roles, every Direct's
// answer (`tape`), every face thrown and every answer given, so a resume, a replay and an undo read the record alone,
// as an attack's house chain does (chain.ts `followAttack`). The run is `runChain` from the start each time, the faces
// and answers recorded so far used first. Its Ends' damage is listed for the table to settle (`damage`), through the
// table's damage setting. `public` is stamped when it is made: every creature it names is one any player may see on a
// live scene. A record that is not public is the DM's alone — and so is anything it asks (`ask.owners` empty). A
// public one names its rule to a player only when the carrier is the party's (a player character or a token a player
// owns; `partyCarrier`); any other creature's reaches a player as "<Creature>: a rule fired" (Tom's C on #1500).
import { parseSaveTags } from './modifiers.js';
import { parseRetry } from './specials.js';
import { runChain, modsText } from './chain.js';
import { carriedNames, conditionKey, parseChainFacts } from './chainTable.js';
import { relation } from '../state/auras.js';
import { activeCombatant } from '../state/encounter.js';
import { flatFooted } from './attack.js';
import { inBand, squaresToFootprint } from './weaponReach.js';
import { nonlethalStatus } from '../state/objects.js';
import { cellOf } from '../grid/cells.js';
import { heightSquares, stepsBetween } from '../grid/measure.js';
import { elevation } from '../state/floors.js';
import { reactionTo, startLine, startsOf } from './starts.js';
import { applyStops, parseDamageStops } from './damageStops.js';
export const TRIGGERS = {
    provokes: { label: 'a creature provokes', roles: ['threatener', 'provoker'], carrier: 'threatener' },
    hit_melee: { label: 'a creature is hit by a melee attack', roles: ['hit', 'attacker'], carrier: 'hit' },
    hit_ranged: { label: 'a creature is hit by a ranged attack', roles: ['hit', 'attacker'], carrier: 'hit' },
    turn_start: { label: 'a turn starts', roles: ['creature'], carrier: 'creature' },
    turn_end: { label: 'a turn ends', roles: ['creature'], carrier: 'creature' },
    drops: { label: 'a creature drops to 0 or fewer', roles: ['creature', 'cause'], carrier: 'creature' },
    damaged: { label: 'a creature takes damage', roles: ['creature', 'cause'], carrier: 'creature' },
    attacks: { label: 'a creature attacks', roles: ['attacker', 'target'], carrier: 'attacker' },
    attacked: { label: 'a creature is attacked', roles: ['target', 'attacker'], carrier: 'target' },
    fells: { label: 'a creature fells a foe in melee', roles: ['attacker', 'felled'], carrier: 'attacker' },
    crits: { label: 'a creature scores a critical hit', roles: ['attacker', 'target'], carrier: 'attacker' },
    hits: { label: 'a creature hits a foe in melee', roles: ['attacker', 'target'], carrier: 'attacker' },
    hits_any: { label: 'a creature hits a foe', roles: ['attacker', 'target'], carrier: 'attacker' },
    misses: { label: 'a creature misses a foe', roles: ['attacker', 'target'], carrier: 'attacker' },
    mount_hit: { label: "a creature's mount is hit by an attack", roles: ['rider', 'hit', 'attacker'], carrier: 'rider' },
    fails_save: { label: 'a creature fails a saving throw', roles: ['creature'], carrier: 'creature' },
    concentration_fails: { label: 'a caster fails its check to keep a spell', roles: ['caster'], carrier: 'caster' },
    // A class feature used from the hot bar (feats.md §10c): the socket's alone, never a When… a block is started from.
    used: { label: 'a creature uses it', roles: ['user'], carrier: 'user' },
};
/** The triggers that fire before an attack's roll (issue 1508). */
export const ATTACK_TRIGGERS = ['attacks', 'attacked'];
/** The triggers after a melee attack (issue 1509): only their entries make that attack again (blocks.js's copy). */
export const AGAIN_TRIGGERS = ['fells', 'hits'];
/** Whether an entry's blocks make the attack that set it off again (issue 1509: Cleave). */
/** Whether an entry can undo the hit that set it off (qa-runs#366): that hit's damage waits for its answer. */
export const negatesHit = (block) => block.nodes.some((n) => n.end?.resolves?.kind === 'negate');
export const makesAttack = (blocks) => blocks.some((b) => b.nodes.some((n) => n.end?.resolves?.kind === 'attack'));
export const TRIGGER_KEYS = Object.keys(TRIGGERS);
export const isTriggerKey = (v) => typeof v === 'string' && Object.prototype.hasOwnProperty.call(TRIGGERS, v);
/** Whether an entry answers a failed save of this kind. */
export function rethrowsSave(block, save) {
    return reactionTo(block, 'fails_save') !== null && (block.save_of === undefined || block.save_of.toLowerCase() === save?.toLowerCase());
}
/** The sheet Direct a save End's value reads: whose save it is and which. Null when its value names no creature's save. */
export function saveOfEnd(value) {
    if (Array.isArray(value)) {
        for (const v of value) {
            const got = saveOfEnd(v);
            if (got)
                return got;
        }
        return null;
    }
    if (typeof value !== 'object' || value === null)
        return null;
    const o = value;
    if (o.source === 'sheet' && o.purpose === 'saving_throw' && typeof o.role === 'string' && typeof o.which === 'string') {
        const tags = o.against === undefined ? null : parseSaveTags(o.against);
        return { role: o.role, which: o.which, ...(tags ? { against: tags } : {}) };
    }
    return saveOfEnd(Object.values(o));
}
/** The words a creature carries an entry by: its name as a slug, its id, and any `also` (the System entry it overrides). */
export function entryKeys(entry, also = []) {
    const keys = [conditionKey(entry.name), conditionKey(entry.id.replace(/_/g, '-')), ...also.map((a) => conditionKey(a.replace(/_/g, '-')))];
    return [...new Set(keys.filter((k) => k !== ''))];
}
/** A feat's name as a sheet lists it, without its choice: "Weapon Focus: longsword" is "Weapon Focus". */
export const featName = (name) => name.replace(/\s*:[^:]*$/, '').trim();
/** Whether a name on the token — a condition, an effect, a linked sheet's feat — is the entry's (see CARRYING above). */
export function carries(scene, id, keys) {
    return namesOn(scene, id).some((n) => keys.includes(n));
}
/**
 * The conditions that leave a creature unable to act, so it threatens nobody (3.5 and Pathfinder alike: a helpless,
 * paralyzed, unconscious or dead creature makes no attack of opportunity; nor a dying, petrified or stunned one, which
 * cannot act either, nor a cowering, dazed or fascinated one, which "can take no actions", nor a nauseated one, which
 * is "unable to attack" — bug check of issue 1506's batch). Matched as condition slugs, as the site's condition
 * entries are named.
 */
export const HELPLESS_CONDITIONS = ['cowering', 'dazed', 'dead', 'dying', 'fascinated', 'helpless', 'nauseated', 'paralyzed', 'petrified', 'stunned', 'unconscious'];
/**
 * The conditions of a grapple, under which a creature makes no attack of opportunity, whatever else it carries (bug
 * check of issues 1500/1506/1507). 3.5's Grappling: "He does not threaten any squares" (a pinned creature is grappling
 * too); Pathfinder's Grappled: "Grappled creatures cannot make attacks of opportunity", and Pinned is "a more severe
 * version of grappled". Each system names only its own, so one list serves both.
 */
export const GRAPPLE_CONDITIONS = ['grappled', 'grappling', 'pinned'];
/**
 * The names that keep a creature conscious and fighting below 0 hit points (bug check of issues 1500/1506/1507): Diehard
 * (3.5: "you may choose to act as if you were disabled"; Pathfinder: "act as if you were disabled, rather than dying"),
 * ferocity (3.5's monsters: "continues to fight without penalty even while disabled or dying"; Pathfinder's universal
 * rule: "remains conscious and can continue fighting even if its hit point total is below 0") and the orc's own. A
 * disabled creature still threatens, as one at exactly 0 hit points does. Carried as a feat, a condition or an effect.
 */
export const FIGHTS_BELOW_ZERO = ['diehard', 'ferocity', 'orc-ferocity'];
/** The names on a token: its conditions, its combatant's effects and its sheet's feats, as condition slugs. */
function namesOn(scene, id) {
    const feats = (scene.objects[id]?.values.feats ?? []).map(featName);
    return [...carriedNames(scene, id), ...feats].flatMap((n) => [conditionKey(n), conditionKey(n.replace(/_/g, '-'))]);
}
/**
 * Whether a token can make no attack of opportunity: below 0 hit points (at exactly 0 it is only disabled, and may act)
 * unless Diehard or ferocity keeps it fighting and it is not dead, unconscious from nonlethal damage (issue 1502), or by
 * a condition above. With the scene, the names its effects carry count as its conditions do.
 */
export function helpless(o, scene) {
    const names = scene ? namesOn(scene, o.id) : (o.values.conditions ?? []).map(conditionKey);
    if (o.dead || names.some((n) => HELPLESS_CONDITIONS.includes(n)))
        return true;
    const hp = o.values.hp;
    if (hp && typeof hp.current === 'number' && hp.current < 0 && !names.some((n) => FIGHTS_BELOW_ZERO.includes(n)))
        return true;
    return nonlethalStatus(hp) === 'unconscious';
}
/**
 * Whether a token makes no attack of opportunity now (bug check of issues 1500/1506/1507): helpless, in a grapple, or
 * flat-footed by the system's `opportunity.until_acted` rule — its condition on the token, or round 1 of a running fight
 * before its turn (`flatFooted`, as the flat-footed AC reads it) — and carrying none of the names that rule excepts.
 */
export function makesNoOpportunity(scene, id, combat) {
    const o = scene.objects[id];
    if (!o)
        return true;
    if (o.noAoo || helpless(o, scene))
        return true;
    const names = namesOn(scene, id);
    if (names.some((n) => GRAPPLE_CONDITIONS.includes(n)))
        return true;
    const until = combat?.opportunity?.untilActed;
    if (until && ((until.condition !== null && names.includes(conditionKey(until.condition))) || flatFooted(scene.encounter, id)))
        return !until.unless.some((u) => names.includes(conditionKey(u)));
    return false;
}
/**
 * Whether the token carries the entry (see CARRYING above): named on it, or everyone's in the encounter, unless turned
 * off. `combat`, the table's combat rules, says who is flat-footed (`makesNoOpportunity`); without it, nobody is.
 */
export function carriesEntry(scene, id, entry, combat) {
    const o = scene.objects[id];
    if (!o || o.kind !== 'token')
        return false;
    if (reactionTo(entry.block, 'provokes') && makesNoOpportunity(scene, id, combat))
        return false;
    if (entry.block.carried_by === 'everyone' && scene.encounter.combatants.some((c) => c.tokens.includes(id)))
        return true;
    return carries(scene, id, entry.keys);
}
/**
 * Whether a token may USE a class feature used at a tap: as `carriesEntry`, but an effect on it does not count. Inspire
 * Courage's effect on the party names the song; it does not teach them to sing it.
 */
export function usesEntry(scene, id, entry) {
    const encounter = { ...scene.encounter, combatants: scene.encounter.combatants.map((c) => ({ ...c, effects: [] })) };
    return carriesEntry({ objects: scene.objects, encounter }, id, entry);
}
const sided = (o) => ({ id: o.id, pc: o.pc, owners: o.owners, ...(o.disposition ? { disposition: o.disposition } : {}) });
/** Whether two tokens are on opposite sides (the auras' side rule, issue 1150): a Neutral is hostile to nobody (qa-runs#1157). */
export const hostile = (a, b) => relation(sided(a), sided(b)) === 'enemy';
/** Whether two tokens are on one side, a creature with itself too (the auras' side rule). */
export const onOneSide = (a, b) => relation(sided(a), sided(b)) === 'ally';
export const friendly = (a, b) => relation(sided(a), sided(b)) === 'ally';
/**
 * A move's provocations: every token hostile to the mover, carrying `carrying`'s entry, whose square or a square next to
 * it the move leaves — once each, however often. Only while an encounter runs; never the mover itself, a prop or a dead token.
 */
export function provocations(scene, moverId, from, route, extent, carrying, 
/**
 * The kind of move's rule (qa-runs#1161 slice B; `MoveKind.provokes`): `none` provokes nothing (a 5-foot step);
 * `after_first` excuses leaving the square it starts in from each threatener `excused` says (the foes it can see: withdraw).
 */
kind) {
    if (kind?.provokes === 'none')
        return [];
    if (!activeCombatant(scene.encounter) || !route.length)
        return [];
    const mover = scene.objects[moverId];
    if (!mover || mover.kind !== 'token')
        return [];
    const cells = [cellOf(scene.grid, extent, from)];
    for (const p of route)
        cells.push(...stepsBetween(cells[cells.length - 1], cellOf(scene.grid, extent, p)));
    const out = [];
    for (const t of Object.values(scene.objects)) {
        if (t.id === moverId || t.kind !== 'token' || t.dead || !hostile(t, mover) || !carrying(t.id))
            continue;
        const at = cellOf(scene.grid, extent, { x: t.x, y: t.y });
        // Height (qa-runs#1160; design/32 §3): a square next to it only within one square of height too — a griffon flying
        // past 40 ft up leaves no square the fighter below threatens.
        const high = heightSquares(elevation(mover, scene.scene) - elevation(t, scene.scene), 'one') > 1;
        const bands = kind?.threat?.bands.get(t.id);
        const dz = elevation(mover, scene.scene) - elevation(t, scene.scene);
        const threatened = (c) => (bands && kind?.threat
            ? bands.some((b) => b.threatens && inBand(b, squaresToFootprint(scene, extent, c, t, dz, kind.threat.diagonal) * kind.threat.perSquare))
            : !high && Math.max(Math.abs(c.i - at.i), Math.abs(c.j - at.j)) <= 1);
        const firstFree = kind?.provokes === 'after_first' && (kind.excused?.(t.id) ?? true);
        const leaves = cells.some((c, k) => k < cells.length - 1 && threatened(c) && (cells[k + 1].i !== c.i || cells[k + 1].j !== c.j)
            && !(kind?.leaves === 'reach' && threatened(cells[k + 1])) && !(firstFree && k === 0) && !kind?.sheltered?.(t.id, c));
        if (leaves)
            out.push({ trigger: 'provokes', roles: { threatener: t.id, provoker: moverId } });
    }
    return out;
}
/** A casting that provokes (`opportunity.casting`): each hostile creature, carrying what makes the attack, that threatens the caster's square. */
export function castProvocations(scene, casterId, extent, carrying, threat) {
    const me = scene.objects[casterId];
    if (!activeCombatant(scene.encounter) || !me || me.kind !== 'token')
        return [];
    const here = cellOf(scene.grid, extent, { x: me.x, y: me.y });
    const out = [];
    for (const t of Object.values(scene.objects)) {
        if (t.id === casterId || t.kind !== 'token' || t.dead || !hostile(t, me) || !carrying(t.id))
            continue;
        const dz = elevation(me, scene.scene) - elevation(t, scene.scene);
        const bands = threat?.bands.get(t.id);
        const at = cellOf(scene.grid, extent, { x: t.x, y: t.y });
        const near = bands && threat ? bands.some((b) => b.threatens && inBand(b, squaresToFootprint(scene, extent, here, t, dz, threat.diagonal) * threat.perSquare))
            : heightSquares(dz, 'one') <= 1 && Math.max(Math.abs(here.i - at.i), Math.abs(here.j - at.j)) <= 1;
        if (near)
            out.push({ trigger: 'provokes', roles: { threatener: t.id, provoker: casterId } });
    }
    return out;
}
/** The turn's change between two states of an encounter: the ending turn's tokens, then the starting turn's. */
export function turnChanges(before, after) {
    const was = activeCombatant(before);
    const now = activeCombatant(after);
    const moved = (was?.id ?? null) !== (now?.id ?? null) || (was !== null && now !== null && before.round !== after.round);
    // Ending the encounter is no turn's end: only a turn moving on, in a running encounter, fires (bug check on issue 1500).
    if (!moved || !after.running)
        return [];
    return [
        ...(was ? was.tokens.map((id) => ({ trigger: 'turn_end', roles: { creature: id } })) : []),
        ...(now ? now.tokens.map((id) => ({ trigger: 'turn_start', roles: { creature: id } })) : []),
    ];
}
/** The tokens whose hit points went from above 0 to 0 or fewer between two states; `cause` when the table knows it. */
export function drops(before, after, cause) {
    const out = [];
    for (const o of Object.values(after.objects)) {
        const was = before.objects[o.id]?.values.hp?.current;
        const now = o.values.hp?.current;
        if (typeof was === 'number' && typeof now === 'number' && was > 0 && now <= 0) {
            out.push({ trigger: 'drops', roles: { creature: o.id, ...(cause && cause !== o.id ? { cause } : {}) } });
        }
    }
    return out;
}
/** The blocks an entry reaches through `use` and `then`, itself first (at most 32). */
export function reachOf(top, library) {
    const blocks = [top];
    const seen = new Set([`${top.id}@${top.version}`]);
    for (let i = 0; i < blocks.length && blocks.length < 32; i++) {
        for (const n of blocks[i].nodes) {
            const refs = [...(n.use ? [n.use] : []), ...Object.values(n.end?.then ?? {}).filter((t) => typeof t !== 'string')];
            for (const r of refs) {
                const k = `${r.block}@${r.version}`;
                const b = library.find((x) => x.id === r.block && x.version === r.version);
                if (b && !seen.has(k)) {
                    seen.add(k);
                    blocks.push(b);
                }
            }
        }
    }
    return blocks;
}
/** The entry's roles filled from the event: `trigger` by the same key, `self` the carrier. */
export function triggerRoles(entry, event) {
    const carrier = event.roles[TRIGGERS[event.trigger].carrier];
    const out = {};
    for (const r of entry.roles) {
        if (r.pick === 'self')
            out[r.key] = carrier;
        else if (r.pick === 'trigger' && event.roles[r.key] !== undefined)
            out[r.key] = event.roles[r.key];
        // A use's creature chosen already (qa-runs#1442: the token a spell was aimed at, checked by the server as one it would offer).
        else if (r.pick === 'one' && r.chooses && event.trigger === 'used' && event.roles[r.key] !== undefined)
            out[r.key] = event.roles[r.key];
    }
    return out;
}
const resolvesOf = (e) => (e.resolves ? {
    kind: e.resolves.kind, onto: e.resolves.onto, ...(e.resolves.amount !== undefined ? { amount: e.resolves.amount } : {}), ...(e.resolves.damageKind ? { damageKind: e.resolves.damageKind } : {}), ...(e.resolves.type ? { type: e.resolves.type } : {}),
    ...(e.resolves.key ? { key: e.resolves.key } : {}), ...(e.resolves.purpose ? { purpose: e.resolves.purpose } : {}), ...(e.resolves.label ? { label: e.resolves.label } : {}), ...(e.resolves.allies ? { allies: true } : {}),
    ...(e.resolves.endsOn ? { endsOn: e.resolves.endsOn } : {}), ...(e.resolves.conceal ? { conceal: e.resolves.conceal } : {}), ...(e.resolves.images ? { images: e.resolves.images } : {}), ...(e.resolves.ward ? { ward: { ...e.resolves.ward, bypass: [...e.resolves.ward.bypass] } } : {}), ...(e.resolves.temp ? { temp: e.resolves.temp } : {}), ...(e.resolves.grow ? { grow: e.resolves.grow } : {}), ...(e.resolves.keen ? { keen: true } : {}), ...(e.resolves.to ? { to: e.resolves.to } : {}), ...(e.resolves.lasts ? { lasts: e.resolves.lasts } : {}), ...(e.resolves.mods ? { mods: e.resolves.mods.map((m) => ({ ...m })) } : {}),
    ...(e.resolves.against ? { against: e.resolves.against } : {}), ...(e.resolves.extra ? { extra: { ...e.resolves.extra } } : {}),
    ...(e.resolves.burst ? { burst: { ...e.resolves.burst } } : {}), ...(e.resolves.turn ? { turn: true } : {}), ...(e.resolves.asks ? { asks: true } : {}), ...(e.resolves.spends ? { spends: true } : {}),
    ...(e.resolves.retry ? { retry: { ...e.resolves.retry } } : {}), ...(e.resolves.how ? { how: e.resolves.how } : {}), ...(e.resolves.rounds !== undefined ? { rounds: e.resolves.rounds } : {}), ...(e.resolves.foes ? { foes: e.resolves.foes.map((x) => ({ ...x })) } : {}), ...(e.resolves.confirm ? { confirm: true } : {}),
} : undefined);
/**
 * Run (or carry on) a trigger's chain from its record: the Directs read from the tape first and from `ctx` after, the
 * recorded faces and answers used before the dice and the people. Returns the record as it now stands: its Ends, the
 * damage they resolve (each settled one kept as it was), and who is asked now or why it stopped.
 */
export function stepTrigger(facts, ctx) {
    const top = facts.blocks[0];
    const tape = new Map(facts.tape);
    const direct = (a) => {
        // The attack it was started before is the record's own (issue 1508), never the scene's.
        if (a.source === 'attack')
            return facts.attack ? (a.key === 'total' ? facts.attack.total ?? null : a.key === 'light' ? facts.attack.light ?? null : facts.attack[a.key] === true) : null;
        const k = JSON.stringify(a);
        if (tape.has(k))
            return tape.get(k);
        const v = ctx.direct(a) ?? null;
        tape.set(k, v);
        return v;
    };
    const lists = new Map(facts.listed ?? []);
    const creatures = (a) => {
        const k = JSON.stringify(a);
        if (lists.has(k))
            return lists.get(k);
        const v = ctx.creatures ? ctx.creatures(a) : [];
        if (v)
            lists.set(k, v.slice(0, 21).map((x) => ({ id: x.id, name: x.name.slice(0, 40) })));
        return v ? lists.get(k) : null;
    };
    // A number no token has is asked of the DM, never a stop (qa-runs 264).
    const full = { ...ctx, direct, creatures, askUnknown: true, rounding: facts.rounding, resolve: (r) => facts.blocks.find((b) => b.id === r.block && b.version === r.version) ?? null };
    const state = { faces: [...facts.faces], choices: [...facts.answers], ...(facts.rest ? { rest: facts.rest } : {}) };
    const step = runChain(top, { roles: { ...facts.roles, ...(facts.each ?? {}) } }, full, state);
    const ends = step.ends.map((e) => {
        // An attack End that hit, with both its creatures: the table notices it as a hit (issue 1500; `struckBy`).
        const end = facts.blocks.find((b) => b.id === e.block && b.version === e.version)?.nodes.find((n) => n.id === e.node)?.end;
        const judge = end?.judge;
        // A branch's button is its own node's (two questions of a block may both have a yes), so the end reads as it was answered.
        const said = e.outcome !== null ? end?.labels?.[e.outcome] : undefined;
        const hit = judge === 'attack' && (e.outcome === 'hit' || e.outcome === 'threat' || e.outcome === 'critical') && e.roles.attacker && e.roles.target;
        // A failed save the record can still throw again (`rethrowChain`): whose it is, and where in the run it stands.
        const asked = judge === 'save' && e.outcome === 'failure' && e.judged?.at ? saveOfEnd(end?.value) : null;
        const again = judge === 'save' && e.judged?.at ? facts.rethrown?.find((r) => r.at === e.judged.at.faces) : undefined;
        const failed = asked && e.roles[asked.role] && !again ? { creature: e.roles[asked.role], which: asked.which, at: e.judged.at } : undefined;
        const threw = judge === 'save' && e.judged?.at ? saveOfEnd(end?.value) : null;
        const saved = threw && e.roles[threw.role] ? { creature: e.roles[threw.role], which: threw.which, ...(threw.against ? { against: [...threw.against] } : {}) } : undefined;
        return { ...(failed ? { failed } : {}), ...(saved ? { saved } : {}), ...(again ? { again: again.by } : {}), name: e.blockName, outcome: said ?? e.outcome, by: e.resolvedBy, ...((judge === 'save' || judge === 'attack') && typeof e.judged?.against === 'number' ? { thrown: { total: e.judged.total, against: e.judged.against } } : {}), ...(e.resolves ? { resolves: resolvesOf(e) } : {}), ...(hit ? { struck: { attacker: e.roles.attacker, target: e.roles.target } } : {}), ...(judge === 'save' && (facts.each || top.roles.some((x) => x.pick === 'each')) && e.roles.target ? { of: e.roles.target } : {}) };
    });
    const damage = ends.flatMap((e) => ((e.resolves?.kind === 'damage' || e.resolves?.kind === 'heal') && e.resolves.amount !== undefined && e.resolves.amount > 0
        ? [{ onto: e.resolves.onto, amount: e.resolves.amount, ...(e.resolves.damageKind === 'nonlethal' ? { kind: 'nonlethal' } : {}), ...(e.resolves.type ? { types: [e.resolves.type] } : {}), ...(e.resolves.kind === 'heal' ? { heal: true } : {}), ...(e.resolves.asks ? { confirm: true } : {}) }] : []))
        .map((d, i) => (facts.damage[i]?.settled && facts.damage[i].onto === d.onto ? { ...d, settled: facts.damage[i].settled } : d));
    const conditions = ends.flatMap((e) => {
        const r = e.resolves;
        if (r?.kind === 'ability' && r.key && r.how && r.amount)
            return [{ onto: r.onto, key: conditionKey(`${r.label ?? r.key} ${r.amount}`), ability: { key: r.key, how: r.how, amount: r.amount }, ...(r.confirm ? { confirm: true } : {}) }];
        if (r?.kind !== 'condition' || !r.key)
            return [];
        const row = { key: r.key, ...(r.rounds !== undefined ? { rounds: r.rounds } : {}), ...(r.retry ? { retry: { ...r.retry } } : {}), ...(r.confirm ? { confirm: true } : {}) };
        return r.foes ? r.foes.map((x) => (x.destroys ? { onto: x.id, name: x.name, key: 'destroyed', destroys: true, ...(r.confirm ? { confirm: true } : {}) } : { onto: x.id, name: x.name, ...row })) : [{ onto: r.onto, ...row }];
    }).map((c, i) => (facts.conditions?.[i]?.settled && facts.conditions[i].onto === c.onto ? { ...c, settled: facts.conditions[i].settled } : c));
    const out = { ...facts, tape: [...tape], faces: [...step.state.faces], answers: [...step.state.choices], ends, damage, ...(conditions.length ? { conditions } : {}), ...(lists.size ? { listed: [...lists] } : {}) };
    delete out.ask;
    delete out.stopped;
    if (!step.state.rest)
        delete out.rest;
    if (step.status === 'paused') {
        const owners = step.ask.who !== 'actor' ? [] : facts.asked ? [...facts.asked] : facts.public ? [...(facts.sides[facts.carrier]?.owners ?? [])] : [];
        const v = step.ask.value;
        // A number asked (qa-runs 264): the DM's alone, named by the creature as the record names it ("Ogre's Strength?").
        if (v)
            out.ask = { owners: [], name: step.ask.blockName, question: (v.creature !== undefined && facts.sides[v.creature] ? `${facts.sides[v.creature].name}'s ${v.what}?` : step.ask.question ?? `${v.what}?`).slice(0, 200), outcomes: [], number: true };
        else
            out.ask = { owners, name: step.ask.blockName, question: step.ask.question ?? `${step.ask.blockName}?`, outcomes: [...step.ask.outcomes], ...(step.ask.labels ? { labels: step.ask.labels.map((l) => l.slice(0, 40)) } : {}) };
        if (step.ask.labels && !step.ask.branch)
            out.named = { ...(facts.named ?? {}), ...Object.fromEntries(step.ask.outcomes.map((o, i) => [o, step.ask.labels[i].slice(0, 40)])) };
    }
    else if (step.status === 'stopped')
        out.stopped = step.reason;
    return out;
}
/**
 * A chain's failed save thrown again (`save.rethrow`): its faces replaced and its creature's run taken from there, what
 * came after it in that run thrown anew. Every other creature's run keeps its dice and answers (`ChainState.rest`).
 */
export function rethrowChain(facts, end, by, faces, ctx) {
    const failed = facts.ends[end]?.failed;
    const dice = ctx.combat?.saves?.dice;
    if (!failed || !dice)
        return null;
    const was = facts.faces.slice(failed.at.faces, failed.at.faces + dice.count);
    const run = failed.at.run;
    return stepTrigger({
        ...facts, faces: [...facts.faces.slice(0, failed.at.faces), ...faces(dice.count, dice.sides)], answers: facts.answers.slice(0, failed.at.answers),
        ...(run ? { rest: { run: run.index, faces: facts.faces.slice(run.faces), choices: facts.answers.slice(run.answers) } } : {}),
        rethrown: [...(facts.rethrown ?? []), { at: failed.at.faces, was, by: by.slice(0, 120) }],
    }, ctx);
}
/**
 * The hits a trigger's chain made (issue 1500): each attack End that hit, as a `hit_melee` event of its own, `from` the
 * record and the End's place in it — so a record run again (an answer resuming it) notices each hit once. The attacks a
 * trigger makes are melee ones (the attack of opportunity's), as no block yet says an attack is ranged.
 */
export function struckBy(facts, rollId) {
    return facts.ends.flatMap((e, i) => (e.struck ? [{ trigger: 'hit_melee', roles: { hit: e.struck.target, attacker: e.struck.attacker }, from: `${rollId}:${i}` }] : []));
}
/** Whether `userId` (a DM when `dm`) may answer what the record asks now. */
export function mayAnswer(facts, userId, dm) {
    if (!facts.ask)
        return false;
    return facts.ask.owners.length ? facts.ask.owners.includes(userId) : dm;
}
/** Whether the record is finished: nothing asked. A settled damage changes nothing here. */
export const triggerDone = (facts) => !facts.ask;
/** The trigger's start, the record with nothing run yet: then `stepTrigger`. */
export function triggerRecord(entry, library, event, scene, sceneKey, rounding, isPublic) {
    const roles = triggerRoles(entry, event);
    const sides = {};
    for (const id of new Set([...Object.values(event.roles), ...Object.values(roles), ...Object.values(event.each ?? {}).flat()])) {
        const o = scene.objects[id];
        if (o)
            sides[id] = { name: o.name.slice(0, 80), named: o.pc || o.shares.name, owners: [...o.owners].slice(0, 16), pc: o.pc };
    }
    return {
        trigger: event.trigger, entry: { id: entry.id, version: entry.version, name: entry.name.slice(0, 120) }, ...(event.from ? { from: event.from.slice(0, 80) } : {}), carrier: event.roles[TRIGGERS[event.trigger].carrier],
        scene: sceneKey, blocks: reachOf(entry, library), roles, ...(event.each ? { each: event.each } : {}), rounding, tape: [], faces: [], answers: [], ends: [], damage: [], sides, public: isPublic,
        ...(startsOf(entry).length > 1 ? { several: true } : {}),
    };
}
// ── Reading it ───────────────────────────────────────────────────────────────────────────────────────────────────────
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isText = (v, max) => typeof v === 'string' && v.length <= max;
const isInt = (v, lo, hi) => typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi;
const isValue = (v) => typeof v === 'number' || typeof v === 'boolean'
    || (isObj(v) && typeof v.bonus === 'number' && Array.isArray(v.dice) && v.dice.every((p) => isObj(p) && isInt(p.count, 0, 1000) && isInt(p.sides, 1, 1000)));
const BY = ['rules', 'actor', 'dm'];
/** A stored trigger record: exact enough to replay, or null. The server stamps it; a replay reads it. */
export function parseTriggerFacts(v) {
    if (!isObj(v) || !isTriggerKey(v.trigger) || !isObj(v.entry) || !isText(v.entry.id, 80) || !isInt(v.entry.version, 1, 1e6) || !isText(v.entry.name, 120))
        return null;
    if (!isText(v.carrier, 64) || !isText(v.scene, 64) || typeof v.public !== 'boolean')
        return null;
    if (v.from !== undefined && !isText(v.from, 80))
        return null;
    if (!Array.isArray(v.blocks) || !v.blocks.length || v.blocks.length > 32)
        return null;
    if (!v.blocks.every((b) => isObj(b) && typeof b.id === 'string' && Number.isInteger(b.version) && typeof b.name === 'string' && Array.isArray(b.nodes) && Array.isArray(b.roles)))
        return null;
    if (!isObj(v.roles) || !Object.values(v.roles).every((x) => typeof x === 'string'))
        return null;
    if (!(v.rounding === null || v.rounding === 'down' || v.rounding === 'up' || v.rounding === 'half_up'))
        return null;
    if (!Array.isArray(v.tape) || v.tape.length > 256 || !v.tape.every((e) => Array.isArray(e) && e.length === 2 && typeof e[0] === 'string' && (e[1] === null || isValue(e[1]))))
        return null;
    if (!Array.isArray(v.faces) || v.faces.length > 1000 || !v.faces.every((x) => isInt(x, 1, 1000)))
        return null;
    if (!Array.isArray(v.answers) || v.answers.length > 64 || !v.answers.every((x) => isText(x, 40)))
        return null;
    if (!Array.isArray(v.ends) || v.ends.length > 256 || !v.ends.every((e) => isObj(e) && typeof e.name === 'string' && (e.outcome === null || typeof e.outcome === 'string') && BY.includes(e.by)
        && (e.resolves === undefined || (isObj(e.resolves) && typeof e.resolves.kind === 'string' && typeof e.resolves.onto === 'string'))
        && (e.again === undefined || isText(e.again, 120))
        && (e.failed === undefined || (isObj(e.failed) && isText(e.failed.creature, 64) && isText(e.failed.which, 40) && isObj(e.failed.at) && isInt(e.failed.at.faces, 0, 10_000) && isInt(e.failed.at.answers, 0, 10_000)
            && (e.failed.at.run === undefined || (isObj(e.failed.at.run) && isInt(e.failed.at.run.index, 0, 10_000) && isInt(e.failed.at.run.faces, 0, 10_000) && isInt(e.failed.at.run.answers, 0, 10_000)))))
        && (e.struck === undefined || (isObj(e.struck) && isText(e.struck.attacker, 64) && isText(e.struck.target, 64)))))
        return null;
    if (v.stops !== undefined && !(isObj(v.stops) && Object.keys(v.stops).length <= 32 && Object.values(v.stops).every((s) => parseDamageStops(s) !== null)))
        return null;
    if (!Array.isArray(v.damage) || v.damage.length > 64 || !v.damage.every((d) => isObj(d) && (d.types === undefined || (Array.isArray(d.types) && d.types.length <= 8 && d.types.every((t) => typeof t === 'string' && t.length <= 40)))) || !v.damage.every((d) => isObj(d) && typeof d.onto === 'string' && isInt(d.amount, 0, 1e6) && (d.kind === undefined || d.kind === 'nonlethal') && (d.heal === undefined || d.heal === true) && (d.confirm === undefined || d.confirm === true)
        && (d.settled === undefined || (isObj(d.settled) && (d.settled.amount === null || isInt(d.settled.amount, 0, 1e6)) && typeof d.settled.auto === 'boolean'))))
        return null;
    if (v.conditions !== undefined && !(Array.isArray(v.conditions) && v.conditions.length <= 64 && v.conditions.every((c) => isObj(c) && isText(c.onto, 64) && isText(c.key, 200) && (c.rounds === undefined || isInt(c.rounds, 1, 100)) && (c.retry === undefined || parseRetry(c.retry) !== null) && (c.ability === undefined || (isObj(c.ability) && isText(c.ability.key, 24) && (c.ability.how === 'damage' || c.ability.how === 'drain') && isInt(c.ability.amount, 1, 999))) && (c.confirm === undefined || c.confirm === true) && (c.destroys === undefined || c.destroys === true) && (c.name === undefined || isText(c.name, 44))
        && (c.settled === undefined || (isObj(c.settled) && typeof c.settled.applied === 'boolean' && typeof c.settled.auto === 'boolean')))))
        return null;
    if (v.ask !== undefined && !(isObj(v.ask) && Array.isArray(v.ask.owners) && v.ask.owners.every((o) => typeof o === 'string') && isText(v.ask.name, 200) && isText(v.ask.question, 200)
        && (v.ask.number === undefined || (v.ask.number === true && Array.isArray(v.ask.outcomes) && v.ask.outcomes.length === 0 && Array.isArray(v.ask.owners) && v.ask.owners.length === 0))
        && Array.isArray(v.ask.outcomes) && (v.ask.outcomes.length > 0 || v.ask.number === true) && v.ask.outcomes.every((o) => isText(o, 40))
        && (v.ask.labels === undefined || (Array.isArray(v.ask.labels) && v.ask.labels.length === v.ask.outcomes.length && v.ask.labels.every((o) => isText(o, 40))))))
        return null;
    if (v.listed !== undefined && !(Array.isArray(v.listed) && v.listed.length <= 16 && v.listed.every((e) => Array.isArray(e) && e.length === 2 && isText(e[0], 400) && Array.isArray(e[1]) && e[1].length <= 21
        && e[1].every((x) => isObj(x) && isText(x.id, 64) && isText(x.name, 40)))))
        return null;
    if (v.named !== undefined && !(isObj(v.named) && Object.keys(v.named).length <= 64 && Object.entries(v.named).every(([k, n]) => k.length <= 64 && isText(n, 40))))
        return null;
    if (v.asked !== undefined && !(Array.isArray(v.asked) && v.asked.length <= 16 && v.asked.every((o) => isText(o, 64))))
        return null;
    if (v.attack !== undefined && !(isObj(v.attack) && Object.entries(v.attack).every(([k, x]) => (k === 'total' ? typeof x === 'number' && Number.isInteger(x) : ['ranged', 'two_handed', 'off_hand', 'light', 'full'].includes(k) && typeof x === 'boolean'))))
        return null;
    if (v.holds !== undefined && !(isObj(v.holds) && isText(v.holds.by, 64) && isObj(v.holds.attack) && JSON.stringify(v.holds.attack).length <= 16_000))
        return null;
    if (v.follows !== undefined && !(isObj(v.follows) && isText(v.follows.by, 64) && isObj(v.follows.attack) && JSON.stringify(v.follows.attack).length <= 16_000))
        return null;
    if (v.rest !== undefined && !(isObj(v.rest) && isInt(v.rest.run, 0, 10_000) && Array.isArray(v.rest.faces) && v.rest.faces.length <= 1000 && v.rest.faces.every((x) => isInt(x, 1, 1000)) && Array.isArray(v.rest.choices) && v.rest.choices.length <= 64 && v.rest.choices.every((x) => isText(x, 40))))
        return null;
    if (v.rethrown !== undefined && !(Array.isArray(v.rethrown) && v.rethrown.length <= 16 && v.rethrown.every((r) => isObj(r) && isInt(r.at, 0, 10_000) && isText(r.by, 120) && Array.isArray(r.was) && r.was.length <= 32 && r.was.every((f) => isInt(f, 1, 1000)))))
        return null;
    if (v.stopped !== undefined && typeof v.stopped !== 'string')
        return null;
    if (v.several !== undefined && v.several !== true)
        return null;
    if (v.limit !== undefined && !(Number.isInteger(v.limit) && v.limit >= 0 && v.limit <= 1000))
        return null;
    if (v.facts !== undefined && !(isObj(v.facts) && JSON.stringify(v.facts).length <= 32_000 && parseChainFacts(v.facts) !== null))
        return null;
    if (v.each !== undefined && !(isObj(v.each) && Object.keys(v.each).length <= 2 && Object.values(v.each).every((l) => Array.isArray(l) && l.length <= 15 && l.every((o) => isText(o, 64)))))
        return null;
    if (!isObj(v.sides) || Object.keys(v.sides).length > 16 || !Object.values(v.sides).every((s) => isObj(s) && isText(s.name, 80) && typeof s.named === 'boolean' && Array.isArray(s.owners) && s.owners.every((o) => typeof o === 'string') && (s.pc === undefined || typeof s.pc === 'boolean')))
        return null;
    return JSON.parse(JSON.stringify(v));
}
const sideName = (facts, id, viewer) => {
    const s = facts.sides[id];
    if (!s)
        return 'a creature';
    return viewer === null || s.named || s.owners.includes(viewer) ? s.name : 'a creature';
};
/** A condition's key as its name: `stunned` is "Stunned". */
export const conditionWords = (key) => { const w = key.replace(/[-_]+/g, ' ').trim(); return w.charAt(0).toUpperCase() + w.slice(1); };
const endText = (facts, e, viewer) => {
    const r = e.resolves;
    const onto = !r ? '' : facts.sides[r.onto] ? sideName(facts, r.onto, viewer) : facts.named?.[r.onto] ?? sideName(facts, r.onto, viewer);
    // Its amounts are the DM's and the carrier's players' (bug check of #1503): anyone else is shown damage only as the
    // hit points that move (#1125), as an attack's working is its attacker's players' alone.
    const amounts = viewer === null || (facts.sides[facts.carrier]?.owners ?? []).includes(viewer);
    const num = (n) => (amounts ? `${n ?? 0} ` : '');
    const what = !r ? '' : r.kind === 'damage' ? ` → ${num(r.amount)}${r.damageKind ? `${r.damageKind} ` : ''}damage to ${onto}` : r.kind === 'sheet' ? ` → ${amounts ? `${r.amount ?? 0} to ` : 'a change to '}${onto}'s ${r.purpose ?? 'sheet'}`
        // A term and a table effect (issue 1508): their numbers are the attack's working's, its maker's players' and the DM's.
        : r.kind === 'term' ? ` → ${amounts ? `${(r.amount ?? 0) < 0 ? '\u2212' : '+'}${Math.abs(r.amount ?? 0)} ` : 'a line '}to ${r.to === 'damage' ? 'the damage' : 'the attack'}`
            // An attack made again (issue 1509): whom it is against, named as shown; its own effect's numbers as a table effect's.
            : r.kind === 'negate' ? ` → ${onto} takes no damage from it`
                : r.kind === 'rethrow' ? ` → ${onto} throws the save again`
                    : r.kind === 'note' ? ` → ${onto}: ${r.label ?? ''}`
                        : r.kind === 'report' || r.kind === 'ability' ? ` → ${r.label ?? 'result'}${amounts ? `: ${r.amount ?? 0}` : ''}`
                            : r.kind === 'burst' ? ` → ${r.label ?? `a burst of ${r.burst?.within ?? 0} feet`}`
                                : r.kind === 'heal' ? ` → ${num(r.amount)}healed on ${facts.sides[r.onto] ? onto : facts.named?.[r.onto] ?? 'a creature'}`
                                    : r.kind === 'attack' ? ` → ${onto} attacks ${r.against ? (facts.sides[r.against] ? sideName(facts, r.against, viewer) : facts.named?.[r.against] ?? 'a creature') : 'nobody'}${r.mods?.length && amounts ? `; ${modsText(r.mods)} on ${onto}` : ''}`
                                        : r.mods ? ` → ${amounts ? `${modsText(r.mods, r.lasts, r.rounds)} on ${r.allies ? `${onto} and allies` : onto}` : `an effect on ${r.allies ? `${onto} and allies` : onto}`}`
                                            : r.kind === 'condition' ? ` → ${conditionWords(r.key ?? 'a condition')}${amounts && r.rounds ? ` for ${r.rounds} round${r.rounds === 1 ? '' : 's'}` : ''} on ${r.turn ? (viewer === null ? `${r.foes?.length ?? 0} in reach` : 'those it reaches') : r.foes ? `${r.foes.length} foe${r.foes.length === 1 ? '' : 's'}` : onto}`
                                                : ` → ${r.kind} ${r.key ?? ''} on ${onto}`.replace(/ {2}/g, ' ');
    // A choice's answer (issue 1508) is the End's outcome: a number is the carrier's players' and the DM's; a creature is named as shown.
    const outcome = e.by === 'rules' && r && e.outcome === 'yes' ? 'done' : e.outcome !== null && facts.sides[e.outcome] ? sideName(facts, e.outcome, viewer) : e.outcome !== null && facts.named?.[e.outcome] ? facts.named[e.outcome]
        : e.outcome !== null && /^\d+$/.test(e.outcome) && !amounts ? 'chosen' : e.outcome;
    const who = e.of ? `${facts.sides[e.of] ? sideName(facts, e.of, viewer) : 'a creature'}: ` : '';
    if (e.thrown) {
        const saved = e.outcome === 'success' ? 'saved' : e.outcome === 'failure' ? 'failed' : null;
        const again = e.again ? `, thrown again by ${e.again}` : '';
        const shown = viewer === null || (saved && amounts) ? ` (${e.thrown.total} vs ${e.thrown.against}${again})` : amounts ? ` (${e.thrown.total}${again})` : again ? ` (${again.slice(2)})` : '';
        return `${e.name}: ${who}${saved ?? outcome ?? 'done'}${shown}${what}`;
    }
    return `${e.name}: ${who}${outcome ?? 'done'}${what}`;
};
/** A rule's own yes or no on the way to the next step says nothing: "Smite Evil: yes" read twice, the second time the person's. */
const worthSaying = (e) => !(e.by === 'rules' && !e.resolves && (e.outcome === 'yes' || e.outcome === 'no'));
/** The head of a trigger's line: "Attack of opportunity (Ogre, as Shelby provokes)". */
function headOf(facts, viewer) {
    const carrier = sideName(facts, facts.carrier, viewer);
    const t = facts.trigger;
    const other = t === 'provokes' ? Object.entries(facts.sides).find(([id]) => id !== facts.carrier)?.[0] : undefined;
    const why = t === 'provokes' && other ? `${sideName(facts, other, viewer)} provokes ${carrier}`
        : t === 'hit_melee' ? `${carrier} is hit in melee`
            : t === 'hit_ranged' ? `${carrier} is hit by a ranged attack`
                : t === 'mount_hit' ? `${carrier}'s mount is hit`
                    : t === 'fails_save' ? `${carrier} fails a save`
                        : t === 'turn_start' ? `${carrier}'s turn starts` : t === 'turn_end' ? `${carrier}'s turn ends`
                            : t === 'used' ? `${carrier} uses it`
                                : t === 'attacks' ? `${carrier} attacks` : t === 'attacked' ? `${carrier} is attacked` : t === 'fells' ? `${carrier} fells a foe` : t === 'hits' ? `${carrier} hits a foe in melee` : t === 'hits_any' ? `${carrier} hits a foe` : t === 'misses' ? `${carrier} misses a foe` : t === 'crits' ? `${carrier} scores a critical hit` : `${carrier} drops`;
    // An entry with several starts (qa-runs#371) says which started it: "Attack roll, as a reaction: Kara provoked".
    if (facts.several) {
        const roles = { [TRIGGERS[t].carrier]: facts.carrier };
        const otherRole = TRIGGERS[t].roles.find((r) => r !== TRIGGERS[t].carrier);
        const second = Object.keys(facts.sides).find((id) => id !== facts.carrier);
        if (otherRole && second)
            roles[otherRole] = second;
        return startLine(facts.entry.name, { how: 'reaction', when: t }, { trigger: t, roles }, (id) => sideName(facts, id, viewer));
    }
    return `${facts.entry.name}: ${why}`;
}
/** The DM's lines for a trigger's record: what set it off, each End, who is asked, why it stopped, the damage. */
export function triggerLines(facts) {
    const out = [headOf(facts, null), ...facts.ends.filter(worthSaying).map((e) => endText(facts, e, null))];
    if (facts.ask)
        out.push(`${facts.ask.question} waiting for ${facts.ask.owners.length ? 'the player' : 'you'} (${facts.ask.number ? 'a number' : (facts.ask.labels ?? facts.ask.outcomes).join(' / ')})`);
    if (facts.stopped)
        out.push(`${facts.entry.name} stopped: ${facts.stopped}`);
    if (!facts.public)
        out.push(facts.entry.id.startsWith('special_') ? "Only you see this: a creature's abilities stay unnamed until the table sees them." : 'Only you see this: it names a creature the players cannot see.');
    return out;
}
/**
 * Whether the record's carrier is the party's (Tom's C on #1500): a player character, or a creature any player owns —
 * the auras' side rule (`allied`). A record stored before `pc` was stamped reads by its owners alone.
 */
export const partyCarrier = (facts) => {
    const s = facts.sides[facts.carrier];
    return Boolean(s && (s.pc === true || s.owners.length > 0));
};
const capital = (t) => t.charAt(0).toUpperCase() + t.slice(1);
/**
 * A player's version (Tom's C on #1500): the rules of the party's creatures by name; any other creature's as "<Creature>:
 * a rule fired" and nothing more — no entry or block name, no question, no reason it stopped — as all of those name the
 * rule. `names` is a co-DM's, who reads the rule's name wherever they are shown the record as a player reads it.
 */
export function playerTrigger(roll, viewerId, opts = {}) {
    const f = roll.trigger;
    if (!f || !f.public)
        return null;
    // An at-use choice (issue 1508) is its maker's: a player who is not the carrier's is sent nothing of it, as an attack's
    // working reaches only its attacker's players (vtt/CLAUDE.md, secrecy); a co-DM reading as a player still reads it.
    if (!opts.names && (ATTACK_TRIGGERS.includes(f.trigger) || AGAIN_TRIGGERS.includes(f.trigger)) && !(f.sides[f.carrier]?.owners ?? []).includes(viewerId) && !(f.asked ?? []).includes(viewerId))
        return null;
    if (!opts.names && !partyCarrier(f)) {
        return { id: roll.id, at: roll.at, lines: [`${capital(sideName(f, f.carrier, viewerId))}: a rule fired`], ...(f.ask ? { waiting: 'the DM' } : {}) };
    }
    // A note is the DM's alone: it says what a stat block says of a creature.
    const lines = [headOf(f, viewerId), ...f.ends.filter((e) => worthSaying(e) && e.resolves?.kind !== 'note').map((e) => endText(f, e, viewerId))];
    if (f.stopped)
        lines.push(`${f.entry.name} stopped; the DM's log says why.`);
    const mine = f.ask && f.ask.owners.includes(viewerId);
    return {
        id: roll.id, at: roll.at, lines,
        ...(mine ? { ask: { question: f.ask.question, outcomes: [...f.ask.outcomes], ...(f.ask.labels ? { labels: [...f.ask.labels] } : {}) } } : {}),
        ...(f.ask && !mine ? { waiting: f.ask.owners.length ? `${sideName(f, f.carrier, viewerId)}'s player` : 'the DM' } : {}),
    };
}
/** The damage a trigger's record still waits to settle on `target`: the first unsettled, or null. */
export const triggerDamageFor = (facts, target) => facts.damage.findIndex((d) => d.onto === target && !d.settled);
/** What a trigger's damage deals after what stops it: only damage whose block names its type. */
export function triggerDealt(facts, d) {
    const s = facts.stops?.[d.onto];
    return s && !d.heal && d.types?.length ? applyStops(s.rules, s.creature, [{ types: d.types, amount: d.amount }], s.overcome) : { total: d.amount, lines: [] };
}
/** The record with that condition settled: applied, or dropped. */
export function settleTriggerCondition(facts, index, applied, auto) {
    return { ...facts, ...(facts.conditions ? { conditions: facts.conditions.map((c, i) => (i === index ? { ...c, settled: { applied, auto } } : c)) } : {}) };
}
/** The record with that damage settled. */
export function settleTriggerDamage(facts, index, amount, auto) {
    return { ...facts, damage: facts.damage.map((d, i) => (i === index ? { ...d, settled: { amount, auto } } : d)) };
}
/** Whether a trigger record may reach a player at all (see THE RECORD above): every creature seen, on a live scene. */
export function triggerPublic(ids, seen, staged) {
    return !staged && ids.every(seen);
}
//# sourceMappingURL=triggers.js.map